import { Permission } from '@up2stay/up2promo-api-client';
import { useCallback, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';

import { AppPageContent } from '@/core/components/app-page-content';
import { AppPageLayout } from '@/core/components/app-page-layout';
import { PageHeader } from '@/core/components/page-header';
import { AnimatedUnderlineTabs } from '@/core/components/ui/tabs-29';
import { PeopleMembersTab } from '@/core/components/workspace/people/people-members-tab';
import { PeopleRolesTab } from '@/core/components/workspace/people/people-roles-tab';
import { PeopleTeamsTab } from '@/core/components/workspace/people/people-teams-tab';
import { useNavigateShell } from '@/core/hooks/use-navigate-shell';
import { usePermissions } from '@/core/hooks/use-permissions';
import { useDictionary } from '@/core/providers/dictionary-provider';
import { shellPaths } from '@/core/router/app-paths';
import { useCurrentWorkspace } from '@/core/store/workspace.store';
import { AppIonPage } from '@/core/components/app-ion-page';

export type PeopleHubTab = 'members' | 'teams' | 'roles';

function parseTab(
  value: string | null,
  availableTabs: PeopleHubTab[],
): PeopleHubTab {
  if (value === 'teams' && availableTabs.includes('teams')) return 'teams';
  if (value === 'roles' && availableTabs.includes('roles')) return 'roles';
  return availableTabs.includes('members')
    ? 'members'
    : (availableTabs[0] ?? 'members');
}

export function PeoplePage() {
  const dict = useDictionary();
  const peopleDict = dict.workspace.people;
  const { navigateShell } = useNavigateShell();
  const location = useLocation();
  const currentWorkspace = useCurrentWorkspace();
  const { can, isLoading: permissionsLoading } = usePermissions();

  const availableTabs = useMemo((): PeopleHubTab[] => {
    const tabs: PeopleHubTab[] = [];
    if (can(Permission.MEMBERS_READ)) tabs.push('members');
    if (can(Permission.TEAMS_READ)) tabs.push('teams');
    if (can(Permission.ROLES_MANAGE)) tabs.push('roles');
    return tabs;
  }, [can]);

  const searchParams = new URLSearchParams(location.search);
  const activeTab = parseTab(searchParams.get('tab'), availableTabs);

  const setTab = useCallback(
    (tab: PeopleHubTab) => {
      const params = new URLSearchParams(location.search);
      if (tab === 'members') {
        params.delete('tab');
      } else {
        params.set('tab', tab);
      }
      const query = params.toString();
      navigateShell(
        query ? `${shellPaths.morePeople}?${query}` : shellPaths.morePeople,
        { intent: 'updateQuery' },
      );
    },
    [location.search, navigateShell],
  );

  useEffect(() => {
    const requested = new URLSearchParams(location.search).get('tab');
    // Until permissions load, every tab looks forbidden; normalizing then
    // dropped ?tab=teams / ?tab=roles on reload (DEV-538).
    if (!requested || permissionsLoading) return;
    const normalized = parseTab(requested, availableTabs);
    if (requested !== normalized) {
      setTab(normalized);
    }
  }, [availableTabs, location.search, permissionsLoading, setTab]);

  const tabs = useMemo(
    () =>
      availableTabs.map((tab) => {
        if (tab === 'members') {
          return {
            name: peopleDict.tabs.members,
            value: 'members',
            content: <PeopleMembersTab onManageRoles={() => setTab('roles')} />,
          };
        }
        if (tab === 'teams') {
          return {
            name: peopleDict.tabs.teams,
            value: 'teams',
            content: <PeopleTeamsTab />,
          };
        }
        return {
          name: peopleDict.tabs.roles,
          value: 'roles',
          content: <PeopleRolesTab />,
        };
      }),
    [availableTabs, peopleDict.tabs, setTab],
  );

  return (
    <AppIonPage>
      <AppPageContent>
        <AppPageLayout className="min-w-0 overflow-x-hidden">
          <PageHeader
            title={peopleDict.title}
            breadcrumb={
              <>
                {dict.nav.navDomainPeople}
                <span className="mx-1.5 text-muted-foreground/70">/</span>
                {dict.nav.people}
              </>
            }
            description={
              currentWorkspace ? undefined : peopleDict.selectWorkspace
            }
          />

          {tabs.length > 0 ? (
            <AnimatedUnderlineTabs
              className="w-full min-w-0"
              tabs={tabs}
              value={activeTab}
              onValueChange={(value) => setTab(parseTab(value, availableTabs))}
            />
          ) : null}
        </AppPageLayout>
      </AppPageContent>
    </AppIonPage>
  );
}
